import { useCallback, useEffect, useRef, useState } from "react";
import { CaretUp, CaretDown, CaretLeft, CaretRight, DeviceMobile, Desktop, ArrowsClockwise } from "@phosphor-icons/react";
import { FACES, LABELS } from "@/lib/siloCubeFaces";
import { DEFAULT_NAV_PREFS, loadNavPrefs, saveNavPrefs, applyFlip, isCoarsePointer } from "@/lib/cubeNav";

const ROWS = {
  desktop: [
    { key: "arrows", label: "Click arrows", three: false },
    { key: "wheel", label: "Mouse wheel · Magic Mouse · trackpad", three: true },
  ],
  mobile: [
    { key: "arrows", label: "Tap arrows", three: false },
    { key: "swipe", label: "Finger swipe", three: false },
  ],
};

const VLABEL = { 1: "Up = up", "-1": "Up = down" };
const HLABEL = { 1: "Left = back", "-1": "Left = forward", 0: "Off" };

// CubeNavControls — Randy (Jun 2026): per-user direction flips for every way you
// can roll the cube (click arrows, mouse wheel / Magic Mouse, finger swipe), with
// a live test pad so you can feel each motion before you leave the studio.
export default function CubeNavControls({ isFounder }) {
  const [prefs, setPrefs] = useState(DEFAULT_NAV_PREFS);
  const [ctx, setCtx] = useState(() => (isCoarsePointer() ? "mobile" : "desktop"));
  const [faceKey, setFaceKey] = useState("center");
  const [nudge, setNudge] = useState(null);
  const wheelAcc = useRef({ d: 0, t: 0, cool: 0 });
  const start = useRef(null);

  useEffect(() => { loadNavPrefs().then((p) => setPrefs(p)); }, []);

  const setAxis = (input, ax, val) => {
    const next = JSON.parse(JSON.stringify(prefs));
    next[ctx][input][ax] = val;
    setPrefs(next);
    saveNavPrefs(next);
  };
  const cycleV = (input) => setAxis(input, "v", prefs[ctx][input].v === 1 ? -1 : 1);
  const cycleH = (input, three) => {
    const cur = prefs[ctx][input].h;
    const order = three ? [1, -1, 0] : [1, -1];
    const idx = order.indexOf(cur);
    setAxis(input, "h", order[(idx + 1) % order.length]);
  };

  const reset = () => {
    const next = JSON.parse(JSON.stringify(prefs));
    next[ctx] = JSON.parse(JSON.stringify(DEFAULT_NAV_PREFS[ctx]));
    setPrefs(next);
    saveNavPrefs(next);
  };

  // ---- live test pad ----
  const doMove = useCallback((rawDir, input) => {
    const axes = prefs[ctx]?.[input];
    const dir = applyFlip(rawDir, axes);
    if (!dir) return;
    setFaceKey((cur) => {
      let next = FACES[cur]?.[dir];
      if (!next && dir === "up") next = "public-qa";
      if (!next && dir === "down") next = "fan-portal";
      if (!next) return cur;
      if (next === "admin" && !isFounder) return cur;
      return next;
    });
    setNudge(dir);
    setTimeout(() => setNudge(null), 220);
  }, [prefs, ctx, isFounder]);

  const onPadWheel = useCallback((e) => {
    e.preventDefault();
    const now = Date.now();
    if (now < wheelAcc.current.cool) return;
    const horizontal = Math.abs(e.deltaX) > Math.abs(e.deltaY);
    if (now - wheelAcc.current.t > 220) wheelAcc.current.d = 0;
    wheelAcc.current.t = now;
    const delta = horizontal ? e.deltaX : e.deltaY;
    wheelAcc.current.d += delta;
    if (Math.abs(wheelAcc.current.d) < 40) return;
    const rawDir = horizontal ? (wheelAcc.current.d > 0 ? "right" : "left") : (wheelAcc.current.d > 0 ? "down" : "up");
    doMove(rawDir, "wheel");
    wheelAcc.current.d = 0;
    wheelAcc.current.cool = now + 320;
  }, [doMove]);

  // native non-passive wheel binding so we can preventDefault page scroll
  const setPadRef = useCallback((node) => {
    if (setPadRef._clean) { setPadRef._clean(); setPadRef._clean = null; }
    if (!node) return;
    node.addEventListener("wheel", onPadWheel, { passive: false });
    setPadRef._clean = () => node.removeEventListener("wheel", onPadWheel);
  }, [onPadWheel]);

  const onPadDown = (e) => { start.current = { x: e.clientX, y: e.clientY }; };
  const onPadUp = (e) => {
    if (!start.current) return;
    const dx = e.clientX - start.current.x;
    const dy = e.clientY - start.current.y;
    start.current = null;
    if (Math.abs(dx) < 30 && Math.abs(dy) < 30) return;
    const rawDir = Math.abs(dx) >= Math.abs(dy) ? (dx > 0 ? "left" : "right") : (dy > 0 ? "down" : "up");
    doMove(rawDir, ctx === "mobile" ? "swipe" : "wheel");
  };

  const nudgeStyle = nudge
    ? { transform: `translate(${nudge === "left" ? -8 : nudge === "right" ? 8 : 0}px, ${nudge === "up" ? -8 : nudge === "down" ? 8 : 0}px)` }
    : { transform: "translate(0,0)" };

  const Flip = ({ label, active, onClick, testid }) => (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`px-2.5 py-1 rounded-md text-[11px] font-medium border transition-colors ${
        active ? "border-[#4A5D4E] bg-[#EEF1ED] text-[#4A5D4E]" : "border-[#E4E2DA] bg-white text-[#6B6A68] hover:text-[#1C1B1A]"
      }`}
    >
      {label}
    </button>
  );

  return (
    <div className="pt-4 mt-3 border-t hairline" data-testid="cube-nav-controls">
      <div className="flex items-center justify-between gap-3 mb-3 max-w-sm mx-auto">
        <p className="text-[10px] uppercase tracking-[0.2em] text-[#6B6A68]">Navigation feel</p>
        <div className="inline-flex rounded-lg border hairline overflow-hidden">
          <button type="button" onClick={() => setCtx("mobile")} data-testid="nav-ctx-mobile"
            className={`px-3 py-1 text-[11px] font-medium flex items-center gap-1 ${ctx === "mobile" ? "bg-[#1C1B1A] text-white" : "bg-white text-[#6B6A68]"}`}>
            <DeviceMobile size={13} weight="duotone" /> Mobile
          </button>
          <button type="button" onClick={() => setCtx("desktop")} data-testid="nav-ctx-desktop"
            className={`px-3 py-1 text-[11px] font-medium flex items-center gap-1 ${ctx === "desktop" ? "bg-[#1C1B1A] text-white" : "bg-white text-[#6B6A68]"}`}>
            <Desktop size={13} weight="duotone" /> Desktop
          </button>
        </div>
      </div>

      <div className="space-y-2 max-w-sm mx-auto">
        {ROWS[ctx].map((row) => (
          <div key={row.key} className="flex items-center justify-between gap-2">
            <span className="text-[11px] text-[#1C1B1A] flex-1 min-w-0 leading-tight">{row.label}</span>
            <div className="flex items-center gap-1.5 shrink-0">
              <Flip
                testid={`nav-${ctx}-${row.key}-v`}
                active={prefs[ctx][row.key].v === -1}
                onClick={() => cycleV(row.key)}
                label={VLABEL[prefs[ctx][row.key].v]}
              />
              <Flip
                testid={`nav-${ctx}-${row.key}-h`}
                active={prefs[ctx][row.key].h !== 1}
                onClick={() => cycleH(row.key, row.three)}
                label={HLABEL[prefs[ctx][row.key].h]}
              />
            </div>
          </div>
        ))}
      </div>

      {/* live test pad */}
      <div className="mt-4 max-w-sm mx-auto">
        <div className="flex items-center justify-between mb-1.5">
          <p className="text-[10px] uppercase tracking-[0.2em] text-[#9b9a96]">Try it — swipe, scroll or tap the arrows</p>
          <button type="button" onClick={reset} data-testid="nav-reset" className="text-[10px] text-[#9b9a96] hover:text-[#1C1B1A] flex items-center gap-1">
            <ArrowsClockwise size={11} /> Reset
          </button>
        </div>
        <div
          ref={setPadRef}
          onPointerDown={onPadDown}
          onPointerUp={onPadUp}
          data-testid="nav-test-pad"
          className="relative h-28 rounded-xl bg-[#1C1B1A] overflow-hidden select-none touch-none flex items-center justify-center"
        >
          <button type="button" onClick={() => doMove("up", "arrows")} data-testid="nav-pad-up" className="absolute top-1 left-1/2 -translate-x-1/2 text-white/45 hover:text-white p-1"><CaretUp size={16} weight="bold" /></button>
          <button type="button" onClick={() => doMove("down", "arrows")} data-testid="nav-pad-down" className="absolute bottom-1 left-1/2 -translate-x-1/2 text-white/45 hover:text-white p-1"><CaretDown size={16} weight="bold" /></button>
          <button type="button" onClick={() => doMove("left", "arrows")} data-testid="nav-pad-left" className="absolute left-1 top-1/2 -translate-y-1/2 text-white/45 hover:text-white p-1"><CaretLeft size={16} weight="bold" /></button>
          <button type="button" onClick={() => doMove("right", "arrows")} data-testid="nav-pad-right" className="absolute right-1 top-1/2 -translate-y-1/2 text-white/45 hover:text-white p-1"><CaretRight size={16} weight="bold" /></button>
          <div className="text-center transition-transform duration-200" style={nudgeStyle}>
            <div className="font-serif-display text-lg text-white" data-testid="nav-pad-face">{LABELS[faceKey]}</div>
            {faceKey !== "center" && <div className="text-[10px] text-white/45 mt-0.5">tap center arrows to keep going</div>}
          </div>
        </div>
      </div>
    </div>
  );
}
